import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { ExternalLink, Newspaper, RefreshCw, Clock, BookOpen, X, ArrowDownNarrowWide } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_COLORS = {
  "t3n.de": "#00e5ff",
  "all-ai.de": "#facc15",
  "netzpolitik.org": "#c084fc",
};

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 86400 * 7) return `vor ${Math.floor(diff / 86400)} T`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
}

function NewsCard({ item, onOpen }) {
  const color = SOURCE_COLORS[item.source] || "#00e5ff";
  return (
    <button
      onClick={() => onOpen(item)}
      data-testid={`news-card-${item.source}`}
      className="group relative bg-[#0c0c0c] border border-white/[0.07] hover:border-[#00e5ff]/40 transition-all duration-300 overflow-hidden flex flex-col text-left"
    >
      {item.image_url ? (
        <div className="relative aspect-video bg-black overflow-hidden">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div
            className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold bg-black/75 backdrop-blur px-2 py-1 border"
            style={{ color, borderColor: `${color}55` }}
          >
            {item.source}
          </div>
        </div>
      ) : (
        <div className="relative aspect-video bg-gradient-to-br from-[#00e5ff]/[0.04] to-transparent flex items-center justify-center border-b border-white/[0.06]">
          <Newspaper size={36} className="text-[#00e5ff]/40" />
          <div
            className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] font-outfit font-semibold bg-black/75 px-2 py-1 border"
            style={{ color, borderColor: `${color}55` }}
          >
            {item.source}
          </div>
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-outfit font-bold text-white text-[16px] leading-snug mb-2 line-clamp-3 group-hover:text-[#00e5ff] transition-colors">
          {item.title}
        </h3>
        {item.teaser && (
          <p className="text-[13px] text-white/60 leading-relaxed line-clamp-3 mb-3 flex-1">
            {item.teaser}
          </p>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06] text-[11px] text-white/40 font-outfit">
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {formatRelative(item.published_at || item.fetched_at)}
          </span>
          <span className="flex items-center gap-1 text-[#00e5ff]/70 group-hover:text-[#00e5ff]">
            Lesen <BookOpen size={11} />
          </span>
        </div>
      </div>
    </button>
  );
}

function ReaderDialog({ item, open, onOpenChange }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setLoading(true);
    setDetail(null);
    axios
      .get(`${API}/news/detail`, { params: { url: item.url }, withCredentials: true })
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail({ ...item, summary: "" }))
      .finally(() => setLoading(false));
  }, [open, item]);

  if (!item) return null;
  const color = SOURCE_COLORS[item.source] || "#00e5ff";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a0a0a] border border-white/10 text-white rounded-none max-w-3xl p-0 max-h-[92vh] flex flex-col overflow-hidden"
        data-testid="news-reader-dialog"
      >
        {item.image_url && (
          <div className="relative aspect-video bg-black overflow-hidden shrink-0 max-h-[280px]">
            <img src={item.image_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <DialogHeader className="px-6 pt-8 pb-2 shrink-0">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-[10px] uppercase tracking-[0.24em] font-outfit font-semibold px-2 py-1 border"
              style={{ color, borderColor: `${color}55`, background: `${color}10` }}
            >
              Quelle: {item.source}
            </span>
            <span className="text-[11px] text-white/40 font-outfit">
              {formatRelative(item.published_at || item.fetched_at)}
            </span>
          </div>
          <DialogTitle className="font-outfit font-bold text-2xl text-white tracking-tight leading-tight pr-10">
            {item.title}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pt-3 pb-6 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Teaser (from OG) */}
          {item.teaser && (
            <p className="text-[15px] text-white/80 leading-relaxed border-l-2 border-[#00e5ff]/40 pl-4 italic">
              {item.teaser}
            </p>
          )}

          {/* AI-generated summary */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-px bg-[#00e5ff]/60" />
              <span className="text-[10px] uppercase tracking-[0.26em] text-[#00e5ff]/80 font-outfit font-semibold">
                KI-Zusammenfassung
              </span>
            </div>
            {loading ? (
              <div className="space-y-2">
                <div className="h-3 w-full bg-white/[0.04] animate-pulse" />
                <div className="h-3 w-5/6 bg-white/[0.04] animate-pulse" />
                <div className="h-3 w-4/6 bg-white/[0.04] animate-pulse" />
              </div>
            ) : detail?.summary ? (
              <p className="text-[15px] text-white/75 leading-relaxed whitespace-pre-wrap" data-testid="news-summary">
                {detail.summary}
              </p>
            ) : (
              <p className="text-[13px] text-white/40 italic">
                Keine KI-Zusammenfassung verfügbar. Öffne den Original-Artikel für alle Details.
              </p>
            )}
          </div>

          {/* Original link */}
          <div className="pt-4 border-t border-white/10">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="news-read-original"
              className="inline-flex items-center gap-2 px-5 h-11 bg-[#00e5ff] text-black hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] font-outfit font-semibold text-sm tracking-wide transition-all"
            >
              Vollständigen Artikel auf {item.source} lesen
              <ExternalLink size={14} />
            </a>
            <p className="text-[11px] text-white/35 mt-3 leading-relaxed">
              Die Zusammenfassung wird automatisch durch KI erstellt. Für die vollständige Recherche und alle Details öffne bitte den Original-Artikel auf {item.source}.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function NewsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [activeSource, setActiveSource] = useState("all");
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | source
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/news/`, { withCredentials: true });
      setItems(data.items || []);
      setSources(data.sources || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 5 * 60 * 1000); return () => clearInterval(iv); }, [load]);

  // Deep-link: /news?open=<urlencoded URL> opens the reader popup directly
  useEffect(() => {
    if (!items.length) return;
    const params = new URLSearchParams(window.location.search);
    const openUrl = params.get("open");
    if (openUrl) {
      const match = items.find((it) => it.url === openUrl);
      if (match) setSelected(match);
    }
  }, [items]);

  const manualRefresh = async () => {
    if (user?.role !== "admin") return;
    setRefreshing(true);
    try {
      await axios.post(`${API}/news/refresh`, {}, { withCredentials: true });
      await load();
    } finally { setRefreshing(false); }
  };

  const filtered = useMemo(() => {
    const base = activeSource === "all" ? items : items.filter((i) => i.source === activeSource);
    const ts = (x) => {
      const t = Date.parse(x.published_at || x.fetched_at || "");
      return isNaN(t) ? 0 : t;
    };
    const copy = [...base];
    if (sortBy === "newest") copy.sort((a, b) => ts(b) - ts(a));
    else if (sortBy === "oldest") copy.sort((a, b) => ts(a) - ts(b));
    else if (sortBy === "source") copy.sort((a, b) => (a.source || "").localeCompare(b.source || "") || ts(b) - ts(a));
    return copy;
  }, [items, activeSource, sortBy]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    sources.forEach((s) => { c[s] = items.filter((i) => i.source === s).length; });
    return c;
  }, [items, sources]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-[#00e5ff]" />
              <span className="font-outfit text-[11px] uppercase tracking-[0.32em] text-[#00e5ff]/80 font-semibold">
                KI-News (automatisch)
              </span>
            </div>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-white" data-testid="news-heading">
              Aktuelle <span className="text-[#00e5ff]">KI-Artikel</span>
            </h1>
            <p className="text-base text-white/55 mt-3 max-w-2xl leading-relaxed">
              Kuratiert aus {sources.join(", ")}. Ein Klick öffnet eine KI-Zusammenfassung; der Original-Artikel liegt jeweils auf der Quellenseite. Aktualisierung stündlich.
            </p>
          </div>
          {user?.role === "admin" && (
            <button
              onClick={manualRefresh}
              disabled={refreshing}
              data-testid="news-refresh-btn"
              className="inline-flex items-center gap-2 px-4 h-11 border border-white/15 hover:border-[#00e5ff]/40 hover:text-[#00e5ff] text-white/70 rounded-none font-outfit text-sm transition-colors"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Aktualisiere…" : "Jetzt aktualisieren"}
            </button>
          )}
        </div>

        <div className="mb-6 text-[12px] text-white/40 border-l-2 border-[#00e5ff]/30 pl-3 py-1 bg-white/[0.015]">
          Titel, Vorschaubilder und Teaser stammen jeweils aus der Originalquelle. [KI]NPUT zeigt eine kuratierte Übersicht mit KI-generierter Zusammenfassung. Für den vollständigen Artikel folgst du dem Quellen-Link.
        </div>
        <div className="mb-8 inline-flex items-start gap-2 px-3 py-2 border border-[#00e5ff]/25 bg-[#00e5ff]/[0.04] text-[12px]" data-testid="news-no-ads-disclaimer">
          <span className="shrink-0 mt-0.5 text-[#00e5ff]/90 font-outfit font-bold text-[10px] uppercase tracking-[0.18em]">Unabhängig</span>
          <span className="text-white/70 leading-relaxed">
            Keine Werbepartnerschaft. Wir empfehlen diese Redaktionen, weil sie aus unserer Sicht gute, sachliche KI-Berichterstattung liefern.
          </span>
        </div>

        {/* Source filter + Sort */}
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="news-filter">
          <button
            onClick={() => setActiveSource("all")}
            data-testid="news-filter-all"
            className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${activeSource === "all" ? "bg-[#00e5ff] text-black border-[#00e5ff] font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
          >
            Alle Quellen <span className="text-[11px] opacity-60">({counts.all || 0})</span>
          </button>
          {sources.map((s) => {
            const active = activeSource === s;
            const color = SOURCE_COLORS[s] || "#00e5ff";
            return (
              <button
                key={s}
                onClick={() => setActiveSource(s)}
                data-testid={`news-filter-${s}`}
                className={`px-3.5 py-2 text-[13px] font-outfit tracking-wide transition-all border ${active ? "font-semibold" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"}`}
                style={active ? { color, background: `${color}14`, borderColor: `${color}80` } : {}}
              >
                {s} <span className="text-[11px] opacity-60">({counts[s] || 0})</span>
              </button>
            );
          })}
          {/* Spacer + Sort */}
          <div className="flex-1" />
          <div className="inline-flex items-center gap-2 border border-white/10 bg-black px-2.5 h-10 focus-within:border-[#00e5ff]/50 transition-colors">
            <ArrowDownNarrowWide size={13} className="text-white/40" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="news-sort"
              className="bg-transparent text-[12.5px] font-outfit text-white/80 focus:outline-none appearance-none cursor-pointer pr-1"
            >
              <option value="newest" className="bg-[#0a0a0a]">Neueste zuerst</option>
              <option value="oldest" className="bg-[#0a0a0a]">Älteste zuerst</option>
              <option value="source" className="bg-[#0a0a0a]">Nach Quelle</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={`skel-news-${i}`} className="h-80 border border-white/[0.05] bg-white/[0.015] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-white/40">
            <Newspaper size={32} className="mx-auto mb-3" />
            <p className="font-outfit">Noch keine Artikel aus dieser Quelle.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
            {filtered.map((n) => <NewsCard key={n.url} item={n} onOpen={setSelected} />)}
          </div>
        )}
      </main>

      <ReaderDialog item={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
